<template>
  <div>
    <v-row>
      <v-col>
        <v-card flat :disabled="disabled" data-locator="file-browser">
          <v-tabs v-model="currentTab" fixed-tabs class="closd-file-browser">
            <v-tab value="documents" data-locator="documents-tab">{{ $t("project.signing-checklist.SigningChecklistClosdFileBrowser.header1") }}</v-tab>
            <v-tab value="todos" data-locator="checklist-attachments-tab">{{ $t("project.signing-checklist.SigningChecklistClosdFileBrowser.header2") }}</v-tab>
          </v-tabs>
          <v-tabs-window v-model="currentTab">
            <v-tabs-window-item value="documents">
              <v-card-text style="max-height: 300px; overflow-y: auto">
                <template v-if="documentsFolder">
                  <template v-if="documentsFolder.children.length !== 0">
                    <AppClosdFildersTree
                      id="documentsPicker"
                      data-locator="documents-picker"
                      v-model="selectedDocuments"
                      :rootFolder="documentsFolder"
                      :multiple="multiple"
                      :files-only="filesOnly"
                      disable-folder-without-file
                      :disable-files-too-large-to-be-signed="true"
                      :maxSignableSize="maxSignableSize"
                      :value="selectedDocuments"
                    >
                      <template v-slot:label="{ item }">
                        <template v-if="item.disabled">
                          <AppTooltip bottom>
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ item.name }}</span>
                            </template>
                            <span>{{$t('project.signing-checklist.SigningChecklistClosdFileBrowser.fileTooLarge')}}</span>
                          </AppTooltip>
                        </template>
                      </template>
                    </AppClosdFildersTree>
                  </template>
                  <template v-else>
                    <div class="text-caption text-center">
                      {{ $t("project.signing-checklist.SigningChecklistClosdFileBrowser.noDocuments") }}
                    </div>
                  </template>
                </template>
                <template v-else>
                  <div class="text-center">
                    <v-progress-circular
                      style="width: 48px; margin: 100px 0"
                      size="48"
                      indeterminate
                    />
                  </div>
                </template>
              </v-card-text>
            </v-tabs-window-item>
            <v-tabs-window-item value="todos">
              <v-card-text style="max-height: 300px; overflow-y: auto">
                <template v-if="sortedTodosFolder">
                  <template v-if="sortedTodosFolder.children.length !== 0">
                    <AppClosdFildersTree
                      id="todosPicker"
                      data-locator="checklist-attachments-picker"
                      v-model="selectedTodos"
                      :rootFolder="todosFolder"
                      :multiple="multiple"
                      :files-only="filesOnly"
                      disable-folder-without-file
                      :disable-files-too-large-to-be-signed="true"
                      :maxSignableSize="maxSignableSize"
                      :value="selectedTodos"
                    >
                      <template v-slot:label="{ item }">
                        <template v-if="item.disabled">
                          <AppTooltip bottom>
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ item.name }}</span>
                            </template>
                            <span>{{$t('project.signing-checklist.SigningChecklistClosdFileBrowser.fileTooLarge')}}</span>
                          </AppTooltip>
                        </template>
                      </template>
                    </AppClosdFildersTree>
                  </template>
                  <template v-else>
                    <div class="text-caption text-center">
                      {{ $t("project.signing-checklist.SigningChecklistClosdFileBrowser.noTasks") }}
                    </div>
                  </template>
                </template>
                <template v-else>
                  <div class="text-center">
                    <v-progress-circular
                      style="width: 48px; margin: 100px 0"
                      size="48"
                      indeterminate
                    />
                  </div>
                </template>
              </v-card-text>
            </v-tabs-window-item>
          </v-tabs-window>
        </v-card>
      </v-col>
    </v-row>
    <div class="d-flex align-center justify-center mt-4">
      <app-button 
        v-if="hasFiles"
        type="outlined"
        variant="neutral"
        :dark="!noFilesSelected && !disabled"
        :disabled="noFilesSelected || disabled"
        data-locator="validate-selection-button"
        @click="exportFiles"
      >
        <app-material-icon name="add" color="inherit" />
        {{ $t("project.signing-checklist.SigningChecklistClosdFileBrowser.okTitle") }}
      </app-button>
    </div>
  </div>
</template>

<script>
import { flatMapDeep, orderBy } from 'lodash-es'
import { createNamespacedHelpers } from 'vuex'

import AppClosdFildersTree from '@/common/app-closd-filders-tree/AppClosdFildersTree.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { isFileLowerThanMaxSignableSize } from '@/common/utils/files'
import { GET_DOCUMENTS_BY_ID } from '@/store/modules/documents/action_types'
import { GET_TODOS_FOLDER } from '@/store/modules/todo/action_types'

const { mapState: mapDocumentsState, mapActions: mapDocumentsActions } = createNamespacedHelpers('documents')
const { mapState: mapTodoState, mapActions: mapTodoActions } = createNamespacedHelpers('todo')

export default {
  name: 'SigningChecklistClosdFileBrowser',
  components: {
    AppClosdFildersTree,
    AppTooltip,
  },
  props: {
    initialTab: {
      type: String,
      default: 'documents',
    },
    mnemo: {
      type: String,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    filesOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxSignableSize: {
      type: [Number, String],
      default: 0,
    },
  },
  data () {
    return {
      selectedRoom: null,
      documentsFolder: null,
      sortedTodosFolder: null,
      closdRooms: null,
      selectedDocuments: [],
      selectedTodos: [],
      selectedTemplates: [],
      currentTab: this.initialTab,
    }
  },
  computed: {
    ...mapDocumentsState(['documents']),
    ...mapTodoState(['todosFolder']),
    noFilesSelected () {
      return this.selectedDocuments.length === 0 && this.selectedTodos.length === 0 && this.selectedTemplates.length === 0
    },
    hasFiles () {
      return this.documentsFolder?.children.length > 0 || this.todoFolderHasFile
    },
    todoFolderHasFile () {
      if (this.sortedTodosFolder) {
        if (this.sortedTodosFolder.children.length > 0) {
          if (this.sortedTodosFolder.children.some(doc => doc.children.length > 0)) {
            return true
          }
        }
      }
      return false
    },
  },
  watch: {
    // Those watchers are there to make sure only one item can be selected at a time
    // When multiple=false
    selectedDocuments (old, current) {
      if (!this.multiple && (current.length + this.selectedTodos.length) > 1) {
        if (this.selectedDocuments.length > 1) {
          this.selectedDocuments = [current.pop()]
        }
        if (this.selectedDocuments.length !== 0 && this.selectedTodos.length !== 0) {
          this.selectedTodos = []
        }
      }
    },
    selectedTodos (old, current) {
      if (!this.multiple && (current.length + this.selectedDocuments.length) > 1) {
        if (this.selectedTodos.length > 1) {
          this.selectedTodos = [current.pop()]
        }
        if (this.selectedTodos.length !== 0 && this.selectedDocuments.length !== 0) {
          this.selectedDocuments = []
        }
      }
    },
  },
  created () {
    this.documentsFolder = null
    this.sortedTodosFolder = null
    this.getDocumentsFolder()
    this.getTodosFolder()
  },
  methods: {
    ...mapDocumentsActions([GET_DOCUMENTS_BY_ID]),
    ...mapTodoActions([GET_TODOS_FOLDER]),
    async getDocumentsFolder () {
      const fileType = ['pdf']
      if (import.meta.env.VUE_APP_ENABLE_WORD_SIGNING) {
        fileType.push('doc', 'docx')
      }
      await this.GET_DOCUMENTS_BY_ID({
        mnemo: this.mnemo,
        queryObject: {
          ignoreTodoFolder: true,
          lazyMode: false,
          fileType,
        },
        id: 0,
      })
      this.documentsFolder = {
        ...this.documents,
        children: orderBy(this.mapSignableItems(this.documents.children, ['type', 'basename'], ['desc', 'asc'])),
      }
    },
    async getTodosFolder () {
      const fileType = ['pdf']
      if (import.meta.env.VUE_APP_ENABLE_WORD_SIGNING) {
        fileType.push('doc', 'docx')
      }
      await this.GET_TODOS_FOLDER({
        mnemo: this.mnemo,
        queryObject: {
          lazyMode: false,
          fileType,
        },
      })
      this.sortedTodosFolder = {
        ...this.todosFolder,
        children: orderBy(this.mapSignableItems(this.todosFolder.children, ['type', 'basename'], ['desc', 'asc'])),
      }
    },
    mapSignableItems (items) {
      return items.map((item) => {
        if (item.size && !isFileLowerThanMaxSignableSize(item, this.maxSignableSize)) {
          item.disabled = true
        } else if (item.type === 'folder') {
          item.children = this.mapSignableItems(item.children)
        }
        return item
      })
    },
    exportFiles (e) {
      if (e) e.preventDefault()
      const flatFiles = this.getFlatFiles().filter(file => isFileLowerThanMaxSignableSize(file, this.maxSignableSize))
      this.$emit('selectedFiles', flatFiles)
      this.selectedDocuments = []
      this.selectedTodos = []
      this.selectedTemplates = []
    },
    getFlatFiles () {
      const flatFiles = []
      for (const file of this.selectedDocuments) {
        if (file.type === 'file') {
          flatFiles.push(file)
        } else if (file.type === 'folder' && file.childrenCount !== 0) {
          flatFiles.push(this.getChildrenFlatFiles(file))
        }
      }
      for (const file of this.selectedTodos) {
        if (file.type === 'file') {
          flatFiles.push(file)
        } else if (file.type === 'folder' && file.childrenCount !== 0) {
          flatFiles.push(this.getChildrenFlatFiles(file))
        }
      }
      // TODO templates
      return flatMapDeep(flatFiles)
    },
    getChildrenFlatFiles (f) {
      const childrenFiles = []
      if (f.hasOwnProperty('children')) {
        for (const c of f.children) {
          childrenFiles.push(this.getChildrenFlatFiles(c))
        }
      } else {
        childrenFiles.push(f)
      }
      return childrenFiles
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.closd-file-browser) {
  color: rgb(var(--v-theme-grey-500));

  .v-tab {
    font-size: 16px;
    letter-spacing: 1.42px;
    max-width: 360px !important;

    &--selected {
      color: rgb(var(--v-theme-primary));
    }
  }
}
</style>
